import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, AlertTriangle, TrendingUp, Info } from "lucide-react";
import { useProjectVarianceDrivers } from "@/hooks/useProjectVarianceDrivers";
import { useState } from "react";
import type { ProjectAlert, AlertSeverity } from "@/hooks/useProjectAlerts";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const pct = (n: number) => `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`;

const varianceColor = (v: number) =>
  v > 0 ? "text-green-600" : v < 0 ? "text-destructive" : "text-muted-foreground";

const ALERT_BADGE_COLORS: Record<AlertSeverity, string> = {
  critical: "bg-destructive/15 text-destructive border-destructive/30",
  warning: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30",
  info: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30",
};

interface Props {
  projectId: string;
  alerts?: ProjectAlert[];
}

export const VarianceExplanationCard = ({ projectId, alerts = [] }: Props) => {
  const { data, isLoading } = useProjectVarianceDrivers(projectId);
  const [committedDriversOpen, setCommittedDriversOpen] = useState(true);
  const [actualDriversOpen, setActualDriversOpen] = useState(true);
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [unattributedOpen, setUnattributedOpen] = useState(false);

  if (isLoading || !data) return null;

  const { summary: s, drivers: d, top_overrides, top_unattributed, data_quality: dq } = data;

  const hasVariance = s.committed_margin_variance !== 0 || s.actual_margin_variance !== 0 ||
    d.override_exposure !== 0 || d.po_overrun !== 0 || d.unattributed_actual !== 0;

  if (!hasVariance && dq.wo_lines_missing_sov_rate === 0) return null;

  // Committed cost drivers (overrides + PO overruns)
  const committedDriversList = [
    { label: "Subcontractor Rate Overrides", amount: d.override_exposure, count: d.override_line_count },
    { label: "Material PO Overruns", amount: d.po_overrun, count: d.po_overrun_line_count },
  ]
    .filter((x) => x.amount !== 0)
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));

  // Actual cost drivers (unattributed expenses)
  const actualDriversList = [
    { label: "Unattributed Actual Cost", amount: d.unattributed_actual, count: d.unattributed_expense_count },
  ].filter((x) => x.amount !== 0);

  const committedCostVariance = s.committed_cost - s.estimated_cost;

  // Determine header icon: alert for erosion, trending-up for improvement
  const overallVariance = s.committed_margin_variance;
  const HeaderIcon = overallVariance < 0 ? AlertTriangle : overallVariance > 0 ? TrendingUp : Info;

  const hasDataQualityNotes = dq.wo_lines_missing_sov_rate > 0 || dq.unattributed_expense_count > 0 || dq.unattributed_po_count > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <HeaderIcon className="h-4 w-4 text-muted-foreground" />
          Variance Explanation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Margin Erosion Summary */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Margin Summary
          </h4>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]"></TableHead>
                <TableHead className="text-right">Estimated</TableHead>
                <TableHead className="text-right">Committed</TableHead>
                <TableHead className="text-right">Actual</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">Cost</TableCell>
                <TableCell className="text-right">{fmt(s.estimated_cost)}</TableCell>
                <TableCell className="text-right">{fmt(s.committed_cost)}</TableCell>
                <TableCell className="text-right">{fmt(s.actual_cost)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Margin</TableCell>
                <TableCell className="text-right">{s.estimated_margin_pct.toFixed(1)}%</TableCell>
                <TableCell className="text-right">{s.committed_margin_pct.toFixed(1)}%</TableCell>
                <TableCell className="text-right">{s.actual_margin_pct.toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Variance</TableCell>
                <TableCell className="text-right text-muted-foreground">—</TableCell>
                <TableCell className={`text-right font-semibold ${varianceColor(s.committed_margin_variance)}`}>
                  {pct(s.committed_margin_variance)}
                </TableCell>
                <TableCell className={`text-right font-semibold ${varianceColor(s.actual_margin_variance)}`}>
                  {pct(s.actual_margin_variance)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Committed Variance Drivers */}
        {committedDriversList.length > 0 && (
          <Collapsible open={committedDriversOpen} onOpenChange={setCommittedDriversOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors w-full">
              <ChevronDown className={`h-3 w-3 transition-transform ${committedDriversOpen ? "" : "-rotate-90"}`} />
              Committed Cost Drivers
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 space-y-1.5">
                {committedDriversList.map((drv) => {
                  // Match alert by category: overrides → override, PO → po_overrun
                  const matchCategory = drv.label.includes("Override") ? "override" : "po_overrun";
                  const matchedAlert = alerts.find((a) => a.category === matchCategory);
                  return (
                    <div key={drv.label} className="flex items-center justify-between rounded-md border px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{drv.label}</span>
                        <Badge variant="outline" className="text-[10px]">{drv.count} lines</Badge>
                        {matchedAlert && (
                          <Badge variant="outline" className={`text-[10px] ${ALERT_BADGE_COLORS[matchedAlert.severity]}`}>
                            {matchedAlert.severity}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-semibold ${drv.amount > 0 ? "text-destructive" : "text-green-600"}`}>
                          {drv.amount > 0 ? "+" : ""}{fmt(drv.amount)}
                        </span>
                        {committedCostVariance !== 0 && (
                          <span className="text-xs text-muted-foreground">
                            {Math.abs(Math.round((drv.amount / committedCostVariance) * 100))}% of committed variance
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Actual Cost Drivers */}
        {actualDriversList.length > 0 && (
          <Collapsible open={actualDriversOpen} onOpenChange={setActualDriversOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors w-full">
              <ChevronDown className={`h-3 w-3 transition-transform ${actualDriversOpen ? "" : "-rotate-90"}`} />
              Actual Cost Drivers
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 space-y-1.5">
                {actualDriversList.map((drv) => {
                  const matchedAlert = alerts.find((a) => a.category === "unattributed");
                  return (
                    <div key={drv.label} className="flex items-center justify-between rounded-md border px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{drv.label}</span>
                        <Badge variant="outline" className="text-[10px]">{drv.count} items</Badge>
                        {matchedAlert && (
                          <Badge variant="outline" className={`text-[10px] ${ALERT_BADGE_COLORS[matchedAlert.severity]}`}>
                            {matchedAlert.severity}
                          </Badge>
                        )}
                      </div>
                      <span className={`text-sm font-semibold ${drv.amount > 0 ? "text-destructive" : "text-green-600"}`}>
                        {fmt(drv.amount)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Override Cost Impact */}
        {top_overrides.length > 0 && (
          <Collapsible open={overridesOpen} onOpenChange={setOverridesOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors w-full">
              <ChevronDown className={`h-3 w-3 transition-transform ${overridesOpen ? "" : "-rotate-90"}`} />
              Override Cost Impact
            </CollapsibleTrigger>
            <CollapsibleContent>
              <Table className="mt-2">
                <TableHeader>
                  <TableRow>
                    <TableHead>SOV Line</TableHead>
                    <TableHead className="text-right">Est. Rate</TableHead>
                    <TableHead className="text-right">Override Rate</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Cost Impact</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {top_overrides.map((o, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm max-w-[200px] truncate">{o.sov_line_description}</TableCell>
                      <TableCell className="text-right text-sm">{fmt(o.estimated_rate)}</TableCell>
                      <TableCell className="text-right text-sm">{fmt(o.override_rate)}</TableCell>
                      <TableCell className="text-right text-sm">{o.quantity}</TableCell>
                      <TableCell className={`text-right text-sm font-semibold ${o.impact > 0 ? "text-destructive" : "text-green-600"}`}>
                        {o.impact > 0 ? "+" : ""}{fmt(o.impact)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                        {o.reason || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-1 text-right text-xs font-semibold text-muted-foreground">
                Total Override Exposure: <span className="text-foreground">{fmt(d.override_exposure)}</span>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Unattributed Actual Cost */}
        {top_unattributed.length > 0 && (
          <Collapsible open={unattributedOpen} onOpenChange={setUnattributedOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors w-full">
              <ChevronDown className={`h-3 w-3 transition-transform ${unattributedOpen ? "" : "-rotate-90"}`} />
              Unattributed Actual Cost
            </CollapsibleTrigger>
            <CollapsibleContent>
              <Table className="mt-2">
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Category</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {top_unattributed.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm max-w-[250px] truncate">{e.description}</TableCell>
                      <TableCell className="text-right text-sm font-semibold">{fmt(e.amount)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{e.category || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-1 text-right text-xs font-semibold text-muted-foreground">
                Total Unattributed: <span className="text-foreground">{fmt(d.unattributed_actual)}</span>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Data Quality Notes */}
        {hasDataQualityNotes && (
          <div className="rounded-md border border-dashed p-3 space-y-1">
            <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              <Info className="h-3 w-3" />
              Data Quality
            </div>
            {dq.wo_lines_missing_sov_rate > 0 && (
              <p className="text-xs text-muted-foreground">
                {dq.wo_lines_missing_sov_rate} work order line{dq.wo_lines_missing_sov_rate !== 1 ? "s" : ""} created before rate tracking lack baseline sub rates — override exposure may be understated.
              </p>
            )}
            {dq.unattributed_expense_count > 0 && (
              <p className="text-xs text-muted-foreground">
                {dq.unattributed_expense_count} approved expense{dq.unattributed_expense_count !== 1 ? "s" : ""} not attributed to an SOV line.
              </p>
            )}
            {dq.unattributed_po_count > 0 && (
              <p className="text-xs text-muted-foreground">
                {dq.unattributed_po_count} purchase order{dq.unattributed_po_count !== 1 ? "s" : ""} not linked to an SOV line — per-line overrun calculation may be incomplete.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
